/**
 * Live twin aggregates — derives the dashboard's categorical / analytics
 * widget data (Defect Pareto, scrap-trend, KPI roll-ups) from the org's
 * LIVE heats instead of an imported spreadsheet.
 *
 * The source rows are the SAME `FoundryBatchRow[]` the Batches screen and the
 * Reports generator read — `toBatchRow(...)` over `useTwinBatchesQuery()` in
 * live mode, the `FX_BATCHES` fixture in stub mode. So a dashboard with NO
 * CSV/Excel import still populates from real production data, and the numbers
 * reconcile with the Batches table + Reports page the user can cross-check.
 *
 * The yield / rejection / defect-tally math is delegated to
 * `computeReport()` (lib/reports/statistics.ts) so there is ONE deterministic
 * derivation of those numbers across the app. The daily scrap-trend series is
 * projected from the same report's `daily` rows.
 *
 * Org-scoped + live-gated: `useTwinBatchesQuery` only fetches when
 * `VITE_USE_LIVE_API=true`, an org is active, and the session is
 * authenticated (see `useLiveEnabled` in lib/api/queries.ts). In stub mode it
 * stays on the fixture seed, so the backend-less demo is unchanged.
 */

import { useMemo } from "react";
import type { TimeseriesPoint } from "@/lib/widgets/types";
import {
  FX_BATCHES,
  type FoundryBatchRow,
} from "@/lib/widgets/__foundry_fixtures";
import { isLiveApi, toBatchRow, useTwinBatchesQuery } from "@/lib/api";
import { heatsInWindow } from "@/lib/reports/lot-report";
import { computeReport } from "@/lib/reports/statistics";
import type { WidgetWindow } from "./use-live-tag-data";

// ────────────────────────────────────────────────────────────────────
// Output shapes — projected to exactly what the dashboard widgets consume.
// ────────────────────────────────────────────────────────────────────

/** One Pareto bar: a defect category + its heat count, sorted desc. Drops
 *  straight into `DefectParetoChart` / `ParetoChart` (`{ name, count }`). */
export interface DefectParetoBar {
  name: string;
  count: number;
}

/** Headline roll-ups across the heat window — the numbers the KPI widget
 *  resolves by bound key (rejection %, parts produced, yield %, …). */
export interface TwinKpiRollups {
  /** Castings accepted ÷ produced, 0–100. **null when produced === 0** — nobody
   *  counted these heats, and a KPI card showing "0 %" asserts that they did
   *  (GATE-SCOPE7). The card renders its honest empty state instead. */
  yieldPct: number | null;
  /** Castings rejected ÷ produced, 0–100. **null when produced === 0.** */
  rejectionPct: number | null;
  /** Total castings produced across the window. */
  partsProduced: number;
  /** Total castings accepted (good pieces). */
  partsAccepted: number;
  /** Total castings rejected (scrap pieces). */
  partsRejected: number;
  /** Number of heats contributing. */
  heats: number;
  /** Heats with status === "fail". */
  failedHeats: number;
}

/** Everything the categorical / analytics dashboard widgets need from live
 *  heats, plus the flags they branch on. */
export interface TwinAggregates {
  /** Defect distribution, desc by count — for the Pareto. */
  defectPareto: DefectParetoBar[];
  /** Daily rejection-rate series (scrap %) — for the scrap-trend timeseries. */
  dailyScrapTrend: TimeseriesPoint[];
  /** KPI roll-ups — for the KPI widget. */
  kpi: TwinKpiRollups;
  /** Number of source heats (0 ⇒ the widgets show an honest empty state). */
  heatCount: number;
  /** Every heat came from an import (a plant that books its scrap on casting lots reads its lots, lot-aggregates.ts). */
  allHeatsImported: boolean;
  /** True while the live heat list is still loading (live mode only). */
  isLoading: boolean;
}

// ────────────────────────────────────────────────────────────────────
// Pure builders — fed `FoundryBatchRow[]`, no React. Testable in isolation
// and reusable by the copilot / reports if needed.
// ────────────────────────────────────────────────────────────────────

/**
 * Defect Pareto bars from heat rows. Counts how many heats recorded each
 * `defectType` (mirrors `computeReport`'s `topDefects` tally), humanises the
 * label ("sand_inclusion" → "sand inclusion") and caps to the top-N for the
 * chart. Empty input ⇒ `[]` so the widget renders an honest empty state.
 */
export function buildDefectPareto(
  rows: readonly FoundryBatchRow[],
  cap = 12,
): DefectParetoBar[] {
  const { totals } = computeReport(rows, "heat");
  return totals.topDefects
    .slice(0, cap)
    .map((d) => ({ name: d.defect.replace(/_/g, " "), count: d.count }));
}

/**
 * Daily scrap-rate (rejection %) series from heat rows. Projects the `daily`
 * report rows — which are already bucketed by UTC calendar day, sorted
 * chronologically — into `{ t, v }` points where `t` is the ISO day key and
 * `v` is that day's rejection percentage. Empty input ⇒ `[]`.
 */
export function buildDailyScrapTrend(
  rows: readonly FoundryBatchRow[],
): TimeseriesPoint[] {
  const report = computeReport(rows, "daily");
  return report.rows
    // Drop the "unknown date" bucket from the trend line — it has no position
    // on a time axis (the report table still shows it; a chart can't).
    .filter((r) => r.key !== "unknown")
    .map((r) => ({ t: r.key, v: r.rejectionPct }));
}

/**
 * KPI roll-ups from heat rows. Reuses `computeReport`'s org-wide totals so the
 * dashboard's yield / rejection / parts numbers match the Reports page exactly.
 */
export function buildKpiRollups(
  rows: readonly FoundryBatchRow[],
): TwinKpiRollups {
  const { totals } = computeReport(rows, "heat");
  return {
    yieldPct: totals.yieldPct,
    rejectionPct: totals.rejectionPct,
    partsProduced: totals.produced,
    partsAccepted: totals.accepted,
    partsRejected: totals.rejected,
    heats: totals.heats,
    failedHeats: totals.failedHeats,
  };
}

/**
 * Resolve a KPI widget's bound key to a live value (+ optional unit). Maps the
 * same key vocabulary the imported-rows `aggregateKpi` understood (rejection /
 * scrap / good / lots) onto the live roll-ups, so re-binding a KPI to the live
 * source is a drop-in. Returns `null` for an unrecognised key so the caller
 * can fall through (to fixtures in stub mode, or an empty state in live mode).
 */
/**
 * `null` (the OUTER null) = this key is not a heat roll-up at all, so the caller
 * falls through to a live tag / fixture. `{ value: null }` = it IS a roll-up and the
 * plant has not counted it — a different answer, and the caller must render the
 * empty state rather than fall through to a fixture number (GATE-SCOPE7).
 */
export function resolveTwinKpi(
  kpi: TwinKpiRollups,
  key: string,
): { value: number | null; unit: string } | null {
  const k = key.toLowerCase();
  if (
    k.includes("rejection") ||
    k.includes("scrap_percentage") ||
    k.includes("scrap_rate") ||
    k.includes("scrap_pct")
  ) {
    return { value: kpi.rejectionPct == null ? null : round2(kpi.rejectionPct), unit: "%" };
  }
  if (k.includes("yield")) {
    return { value: kpi.yieldPct == null ? null : round2(kpi.yieldPct), unit: "%" };
  }
  if (k.includes("scrap_pieces") || k.includes("rejected")) {
    return { value: kpi.partsRejected, unit: "pcs" };
  }
  if (
    k.includes("good") ||
    k.includes("accepted") ||
    k.includes("produced") ||
    k.includes("parts")
  ) {
    return { value: kpi.partsProduced, unit: "pcs" };
  }
  if (k.includes("heat") || k.includes("lots")) {
    return { value: kpi.heats, unit: "heats" };
  }
  return null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

// ────────────────────────────────────────────────────────────────────
// Hook — the single entry point the dashboard widgets use.
// ────────────────────────────────────────────────────────────────────

/**
 * Live twin aggregates for the dashboard. Reads the org's heats (same source
 * as Batches + Reports) and derives the Pareto / scrap-trend / KPI shapes.
 *
 * Hook is unconditional (React rules); the underlying query is disabled in
 * stub mode, where this returns the fixture-derived aggregates instead. The
 * returned `heatCount` is what the widgets branch on for their empty state —
 * 0 heats ⇒ "Run heats to populate this".
 */
export function useTwinAggregates(dataWindow?: WidgetWindow): TwinAggregates {
  const batchesQuery = useTwinBatchesQuery();
  const from = dataWindow?.from;
  const to = dataWindow?.to;

  const rows = useMemo<FoundryBatchRow[]>(
    () =>
      isLiveApi ? (batchesQuery.data?.items.map(toBatchRow) ?? []) : FX_BATCHES,
    [batchesQuery.data],
  );

  return useMemo<TwinAggregates>(
    () => ({
      ...twinAggregatesOf(rows, from && to ? { from, to } : undefined),
      isLoading: isLiveApi && batchesQuery.isLoading,
    }),
    [rows, from, to, batchesQuery.isLoading],
  );
}

/**
 * The aggregates of the heats a widget reads: every loaded heat, or, for a pinned window, the heats inside it, as the
 * reports read them (lib/reports/lot-report heatsInWindow and reportsFromLots). A heat outside a pinned window neither
 * decides whether the window reads its lots nor lands in its figures: a Run Heat in September never turns an August
 * board back to heats (#994 review, MEDIUM-2). The loaded heats are the list's first page (the newest 200). Pure.
 */
export function twinAggregatesOf(
  all: readonly FoundryBatchRow[],
  dataWindow?: WidgetWindow,
): Omit<TwinAggregates, "isLoading"> {
  const rows = dataWindow ? heatsInWindow(all, dataWindow) : [...all];
  return {
    defectPareto: buildDefectPareto(rows),
    dailyScrapTrend: buildDailyScrapTrend(rows),
    kpi: buildKpiRollups(rows),
    heatCount: rows.length,
    allHeatsImported: rows.length > 0 && rows.every((r) => r.source === "import"),
  };
}
